import { addThousandsSeparator, paymentFrequency } from "utils/helpers";
import { kafeneWeeksToMonths } from "containers/MultistepForm/helpers";

export const getOfferPromo = (
  offer,
  cartTotal,
  { onlySnap = false } = {}
) => {
  if (!offer || !offer.promo_available) return null;
  const provider = offer.specific_provider_name;

  switch (provider) {
    case "koalafi":
    case "aff":
    case "paytomorrow":
      return 50;
    case "ownlease":
      return 65;
    case "snap":
      return onlySnap ? null : 50;
    case "uown":
      if (cartTotal >= 1400) return 100;
      if (cartTotal >= 500) return 50;
      return null;
    case "katapult2":
      return offer.promotion && offer.promotion.available
        ? offer.promotion.amount
        : null;
    case "kafene": {
      const leasePromo =
        offer.options && offer.options[0] && offer.options[0].lease_promotion;
      return leasePromo && leasePromo.amount ? leasePromo.amount : null;
    }
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Single source of truth for the "offer summary" fields displayed in offer
// cards. Mirrors the props that each per-provider component passes to
// OfferHeader (dueToday / paymentPeriod / paymentFrequency) so that
// CartApprovalScreen's PaymentPlanCard shows the same values as the regular
// ExpandedOfferBlock for the same offer.
//
// Returns:
//   {
//     option,            // the default-selected option object (mirrors the
//                        //   componentDidMount picks of each Offer component)
//     dueToday,          // number | string | { amount, info } | 0 — matches
//                        //   the shape OfferHeader expects
//     monthsLabel,       // pre-formatted label like "12 Months", "up to 12
//                        //   Months", "13 Weeks", null when unavailable
//     paymentFrequency,  // pre-formatted "$X/month" string (some providers
//                        //   use raw payment_period_rate verbatim)
//   }
//
// IMPORTANT: when you change the default option selection logic or any of the
// per-provider formatting in the Offer components under Step3/, mirror the
// change here. Until per-provider Offer components are migrated to consume
// this helper directly, the two locations have to stay in sync by hand.
// ---------------------------------------------------------------------------

const pickDefaultOption = (provider, options) => {
  if (!options || !options.length) return null;
  const last = options[options.length - 1];
  switch (provider) {
    case "paytomorrow":
    case "koalafi":
    case "progressive":
    case "flexshopper":
    case "creditkey":
      return last;
    case "okinus":
      return options.reduce(
        (best, cur) => (+best.maxLoan > +cur.maxLoan ? best : cur),
        options[0]
      );
    default:
      return options[0];
  }
};

const resolveDueToday = (provider, option, offer) => {
  switch (provider) {
    case "paytomorrow":
      return offer.specific_provider_name === "bread"
        ? 0
        : addThousandsSeparator(option.initial_payment);
    case "aff":
    case "genesis":
    case "ownlease":
    case "koalafi":
      return 0;
    case "flexshopper":
      return (
        (option.payment_period_rate &&
          option.payment_period_rate.replace(/[^0-9.]+/g, "")) ||
        0
      );
    case "zip":
    case "kornerstone":
      return addThousandsSeparator(option.first_payment);
    case "kafene": {
      const base = option.initial_payment_base != null ? option.initial_payment_base : 0;
      return base > 0
        ? { amount: addThousandsSeparator(base), info: "(+ taxes)" }
        : 0;
    }
    case "progressive":
      return {
        amount: addThousandsSeparator(option.initial_payment) || 1,
        info: "(plus applicable taxes)",
      };
    case "snap":
      return option.initial_payment
        ? addThousandsSeparator(option.initial_payment)
        : 0;
    default:
      return option.initial_payment != null
        ? addThousandsSeparator(option.initial_payment)
        : 0;
  }
};

const resolveMonthsLabel = (provider, option, options) => {
  const period = option.payment_period;
  switch (provider) {
    case "paytomorrow":
      return `up to ${period} Months`;
    case "flexshopper":
      // Per Flexshopper3Offer — period is hardcoded in the header.
      return "13 Months";
    case "zip":
      return `${period} Weeks`;
    case "kafene":
      return `${kafeneWeeksToMonths(period)} Months`;
    case "creditkey":
      return options.length === 1
        ? `${period} Months`
        : `up to ${period} Months`;
    case "uown":
      return period ? `${period} Months` : null;
    default:
      return period != null ? `${period} Months` : null;
  }
};

const resolvePaymentFrequency = (provider, option) => {
  switch (provider) {
    case "ownlease":
      // OwnleaseOffer feeds payment_period_rate verbatim as the frequency label.
      return option.payment_period_rate
        ? option.payment_period_rate.replace(/\s/g, "").toLowerCase()
        : "";
    case "flexshopper":
      // Flexshopper3Offer passes payment_period_rate as the full frequency string.
      return option.payment_period_rate || "";
    default:
      return paymentFrequency(option);
  }
};

export const getOfferSummary = (offer) => {
  if (!offer) return null;
  const provider = offer.provider;
  const option = pickDefaultOption(provider, offer.options);
  if (!option) return null;
  return {
    option,
    dueToday: resolveDueToday(provider, option, offer),
    monthsLabel: resolveMonthsLabel(provider, option, offer.options || []),
    paymentFrequency: resolvePaymentFrequency(provider, option),
  };
};

export const buildSelectedOfferPayload = (offer) => {
  if (!offer) return { selectedOffer: null };
  const summary = getOfferSummary(offer);
  const option = (summary && summary.option) || {};

  const selectedOffer = {
    ...option,
    app_id: offer.app_id,
    provider: offer.provider,
    specific_provider_name: offer.specific_provider_name,
    payment_schedule: offer.payment_schedule,
    cart_total: offer.cart_total,
    promo: offer.promo,
    promo_available: offer.promo_available,
    approved_amount: offer.approved_amount,
    bank_data_is_required: offer.bank_data_is_required,
    banking_data: offer.banking_data,
    authentication_is_required: offer.authentication_is_required,
  };

  // Provider-specific extras that per-provider components splice in.
  if (offer.provider === "koalafi") {
    selectedOffer.order_id = offer.order_id;
    selectedOffer.offer_id = option.offer_id;
    selectedOffer.pdi = offer.pdi;
    selectedOffer.hybridToken = offer.hybrid_token;
  }
  if (offer.provider === "progressive") {
    selectedOffer.contractUrl = offer.contractUrl;
  }

  // Paytomorrow / providers that route via accept_the_offer need offer_id forwarded as the body.
  const body = option.offer_id ? { offer_id: option.offer_id } : undefined;

  return { selectedOffer, body };
};
